import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, input, ViewEncapsulation } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { InformationStatus } from '@ideals/types';
import { TranslatedContentModule } from '@ideals/ui-components/translated-content';

import { IntercomModule } from '../../intercom';
import { Alert } from '../models';
import { PageMessagesService } from '../page-messages.service';

const ICON_CLASSES = {
  [InformationStatus.Success]: 'icons-check-circle',
  [InformationStatus.Warning]: 'icons-exclamation-triangle',
  [InformationStatus.Danger]: 'icons-exclamation-octagon',
  [InformationStatus.Information]: 'icons-info-circle',
};

@Component({
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({
          transform: 'translateY(100%)',
          opacity: 0,
        }),
        animate('300ms ease-out', style({
          transform: 'translateY(0)',
          opacity: 1,
        })),
      ]),
      transition(':leave', [
        style({
          opacity: 1,
        }),
        animate('300ms ease-in', style({
          marginTop: 'calc(-1 * var(--toast-height, 0px))',
          opacity: 0,
        })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'ideals-toast',
    '[class.danger]': 'alert().type === InformationStatus.Danger',
    '[class.success]': 'alert().type === InformationStatus.Success',
    '[class.warning]': 'alert().type === InformationStatus.Warning',
    '[class.information]': 'alert().type === InformationStatus.Information',
    '[class]': 'alert().customClass ?? ""',
    '[@toastAnimation]': '',
  },
  imports: [
    IntercomModule,
    NgClass,
    NgTemplateOutlet,
    TranslateModule,
    TranslatedContentModule,
  ],
  selector: 'ideals-alert',
  styleUrl: './alert.component.scss',
  templateUrl: './alert.component.html',
})
export class AlertComponent implements AfterViewInit {
  readonly alert = input.required<Alert>();

  readonly #element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  readonly #pageMessagesService = inject(PageMessagesService);

  readonly InformationStatus = InformationStatus;

  ngAfterViewInit(): void {
    this.#element.style.setProperty('--toast-height', `${this.#element.offsetHeight}px`);
  }

  get iconClass(): string {
    return ICON_CLASSES[this.alert().type];
  }

  close(): void {
    this.#pageMessagesService.close(this.alert());
  }
}
